import { useShow, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Card,
  Descriptions,
  Tag,
  Avatar,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Popconfirm,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Table,
  Tooltip,
  Segmented,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, StopOutlined, CheckOutlined, EditOutlined, WalletOutlined, PlusOutlined, MinusOutlined, HistoryOutlined, PhoneOutlined, BellOutlined } from '@ant-design/icons';
import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';
import { CallNotesModal } from '../../components/CallNotesModal';
import { PushNotificationModal } from '../../components/PushNotificationModal';

interface User {
  id: number;
  fullName: string | null;
  phone: string;
  currentRole: 'CLIENT' | 'DRIVER' | 'ADMIN';
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
  photoUrl: string | null;
  rating: number;
  totalTrips: number;
  balance: number;
  bonusBalance: number;
  walletBalance: number;
  isDriverVerified: boolean;
  createdAt: string;
  blockedAt: string | null;
  blockedReason: string | null;
  homeRegion?: { id: number; nameUz: string; nameRu: string } | null;
  fcmToken?: string | null;
}

export const UserShow = () => {
  const { goBack, list, edit } = useNavigation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const { queryResult } = useShow<User>();
  const { data, isLoading, refetch } = queryResult;
  const user = data?.data;

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [callNotesOpen, setCallNotesOpen] = useState(false);

  // Push-notification modal state
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushSending, setPushSending] = useState(false);

  const sendPushToUser = async () => {
    if (!user?.id) return;
    if (!pushTitle.trim() || !pushBody.trim()) {
      message.warning('Введите заголовок и текст уведомления');
      return;
    }
    setPushSending(true);
    try {
      const res = await apiClient.post('/admin/notifications/send', {
        title: pushTitle.trim(),
        body: pushBody.trim(),
        targetType: 'USER',
        targetId: user.id,
      });
      const delivered = res.data?.delivered ?? 0;
      const failed = res.data?.failed ?? 0;
      if (delivered > 0) {
        message.success(`✅ Уведомление доставлено`);
      } else if (failed > 0) {
        message.error(
          'FCM не смог доставить (возможно, у пользователя нет push-токена)',
        );
      } else {
        message.info('Отправлено, но FCM не подтвердил доставку');
      }
      setPushModalOpen(false);
      setPushTitle('');
      setPushBody('');
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          'Не удалось отправить уведомление',
      );
    } finally {
      setPushSending(false);
    }
  };
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletAdjustType, setWalletAdjustType] = useState<'add' | 'deduct'>('add');
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [walletAdjusting, setWalletAdjusting] = useState(false);
  const [editForm] = Form.useForm();
  const [walletForm] = Form.useForm();

  // Trip history state
  const [tripHistory, setTripHistory] = useState<any[]>([]);
  const [tripHistoryLoading, setTripHistoryLoading] = useState(false);

  const loadTripHistory = useCallback(async () => {
    if (!user?.id) return;
    setTripHistoryLoading(true);
    try {
      const res = await apiClient.get('/admin/trips', {
        params: { clientId: user.id, limit: 20 },
      });
      const items = res.data?.data || res.data?.items || [];
      setTripHistory(Array.isArray(items) ? items : []);
    } catch {
      // silent
    } finally {
      setTripHistoryLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) loadTripHistory();
  }, [user?.id, loadTripHistory]);

  const handleBlock = async () => {
    if (!blockReason.trim()) {
      message.error(t('users.enterBlockReason'));
      return;
    }

    setBlocking(true);
    try {
      await apiClient.post(`/admin/users/${user?.id}/block`, {
        reason: blockReason,
      });
      message.success(t('users.userBlocked'));
      setBlockModalOpen(false);
      setBlockReason('');
      refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async () => {
    try {
      await apiClient.post(`/admin/users/${user?.id}/unblock`);
      message.success(t('users.userUnblocked'));
      refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleEdit = async (values: { fullName: string }) => {
    try {
      await apiClient.put(`/admin/users/${user?.id}`, values);
      message.success(t('users.userEdited'));
      setEditModalOpen(false);
      refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleWalletAdjust = async (values: { amount: number; reason: string }) => {
    setWalletAdjusting(true);
    try {
      await apiClient.post(`/admin/users/${user?.id}/adjust-wallet`, {
        amount: values.amount,
        reason: values.reason,
        type: walletAdjustType,
      });
      message.success(
        walletAdjustType === 'add' ? t('users.balanceAdded') : t('users.balanceDeducted')
      );
      setWalletModalOpen(false);
      walletForm.resetFields();
      refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setWalletAdjusting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' ' + t('common.sum');
  };

  return (
    <>
      <Show
        isLoading={isLoading}
        title={t('table.user')}
        headerButtons={
          <Space>
            <Tooltip title="Заметки звонков (журнал операторов)">
              <Button
                icon={<PhoneOutlined style={{ color: '#10B981' }} />}
                onClick={() => setCallNotesOpen(true)}
                disabled={!user?.id}
              >
                Звонки
              </Button>
            </Tooltip>
            <Tooltip
              title={
                user?.fcmToken
                  ? 'Отправить push-уведомление пользователю'
                  : 'У пользователя нет FCM-токена — push не дойдёт'
              }
            >
              <Button
                icon={<BellOutlined />}
                onClick={() => setPushModalOpen(true)}
                disabled={!user?.id}
              >
                Уведомление
              </Button>
            </Tooltip>
            {can('users', 'update') && (
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  editForm.setFieldsValue({ fullName: user?.fullName || '' });
                  setEditModalOpen(true);
                }}
              >
                {t('common.edit')}
              </Button>
            )}
            {can('users', 'block') && (
              user?.status === 'BLOCKED' ? (
                <Popconfirm
                  title={t('users.unblockConfirm')}
                  onConfirm={handleUnblock}
                >
                  <Button type="primary" icon={<CheckOutlined />}>
                    {t('common.unblock')}
                  </Button>
                </Popconfirm>
              ) : (
                <Button danger icon={<StopOutlined />} onClick={() => setBlockModalOpen(true)}>
                  {t('common.block')}
                </Button>
              )
            )}
          </Space>
        }
      >
        {/* Phase 3.6 — dual-role users (verified drivers viewing their
            passenger profile) get a one-click toggle to the driver view.
            We swap pages via React Router rather than re-rendering inline
            because DriverShow is a 2400-line tree we don't want to fork. */}
        {user?.id && user.isDriverVerified && (
          <div style={{ marginBottom: 16 }}>
            <Segmented<'client' | 'driver'>
              value="client"
              onChange={(val) => {
                if (val === 'driver') {
                  navigate(`/drivers/${user.id}`, { replace: true });
                }
              }}
              options={[
                { label: t('users.viewAsPassenger'), value: 'client' },
                { label: t('users.viewAsDriver'), value: 'driver' },
              ]}
            />
          </div>
        )}
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card>
              <div className="text-center">
                <Avatar
                  size={100}
                  src={user?.photoUrl}
                  icon={<UserOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <h3>{user?.fullName || t('common.noName')}</h3>
                <p style={{ color: '#7F8C8D' }}>{user?.phone}</p>
                <Space>
                  <Tag
                    color={
                      user?.currentRole === 'DRIVER'
                        ? 'blue'
                        : user?.currentRole === 'ADMIN'
                          ? 'purple'
                          : 'default'
                    }
                  >
                    {user?.currentRole === 'DRIVER'
                      ? t('role.driver')
                      : user?.currentRole === 'ADMIN'
                        ? t('role.admin')
                        : t('role.client')}
                  </Tag>
                  <Tag
                    color={
                      user?.status === 'ONLINE'
                        ? 'green'
                        : user?.status === 'BLOCKED'
                          ? 'red'
                          : 'default'
                    }
                  >
                    {user?.status}
                  </Tag>
                </Space>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card title={t('users.statistics')} style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title={t('table.rating')} value={user?.rating != null ? Number(user.rating).toFixed(1) : '-'} />
                </Col>
                <Col span={6}>
                  <Statistic title={t('table.trips')} value={user?.totalTrips || 0} />
                </Col>
                <Col span={6}>
                  <Statistic
                    title={t('users.balance')}
                    value={formatCurrency(user?.balance || 0)}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title={t('users.bonusBalance')}
                    value={formatCurrency(user?.bonusBalance || 0)}
                  />
                </Col>
              </Row>
            </Card>

            <Card title={t('users.information')} style={{ marginBottom: 24 }}>
              <Descriptions column={2}>
                <Descriptions.Item label={t('common.id')}>{user?.id}</Descriptions.Item>
                <Descriptions.Item label={t('common.phone')}>{user?.phone}</Descriptions.Item>
                <Descriptions.Item label={t('users.name')}>
                  {user?.fullName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('table.role')}>
                  {user?.currentRole === 'DRIVER' ? t('role.driver') : t('role.client')}
                </Descriptions.Item>
                <Descriptions.Item label="Viloyat">
                  {user?.homeRegion?.nameUz || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('table.verified')}>
                  {user?.isDriverVerified ? (
                    <Tag color="green">{t('common.yes')}</Tag>
                  ) : (
                    <Tag color="default">{t('common.no')}</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('table.registered')}>
                  {user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString('uz-UZ')
                    : '-'}
                </Descriptions.Item>
                {user?.blockedAt && (
                  <>
                    <Descriptions.Item label={t('status.blocked')}>
                      {new Date(user.blockedAt).toLocaleDateString('uz-UZ')}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('users.blockReason')}>
                      {user.blockedReason || '-'}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Card>

            <Card
              title={
                <Space>
                  <WalletOutlined />
                  <span>{t('users.wallet')}</span>
                </Space>
              }
            >
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <Statistic
                    title={t('users.balance')}
                    value={formatCurrency(user?.balance || 0)}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t('users.bonusBalance')}
                    value={formatCurrency(user?.bonusBalance || 0)}
                  />
                </Col>
              </Row>
              {can('users', 'update') && (
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setWalletAdjustType('add');
                      walletForm.resetFields();
                      setWalletModalOpen(true);
                    }}
                  >
                    {t('users.addFunds')}
                  </Button>
                  <Button
                    danger
                    icon={<MinusOutlined />}
                    onClick={() => {
                      setWalletAdjustType('deduct');
                      walletForm.resetFields();
                      setWalletModalOpen(true);
                    }}
                  >
                    {t('users.deductFunds')}
                  </Button>
                </Space>
              )}
            </Card>
          </Col>
        </Row>

        {/* Trip History Section */}
        <Card
          title={
            <Space>
              <HistoryOutlined />
              <span>{t('users.tripHistory')}</span>
            </Space>
          }
          style={{ marginTop: 24 }}
          loading={tripHistoryLoading}
        >
          <Table
            dataSource={tripHistory}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10, size: 'small' }}
            locale={{ emptyText: t('users.tripsNotFound') }}
            columns={[
              {
                title: t('common.id'),
                dataIndex: 'id',
                key: 'id',
                width: 70,
              },
              {
                title: t('common.type'),
                dataIndex: 'tripType',
                key: 'tripType',
                width: 140,
                render: (type: string) => {
                  const labels: Record<string, { text: string; color: string }> = {
                    INTERREGION_TAXI: { text: t('tripType.interregionTaxi'), color: 'blue' },
                    INTERCITY_TAXI: { text: t('tripType.shaharlarAro'), color: 'cyan' },
                    POSTAGE: { text: t('tripType.postage'), color: 'purple' },
                    YOL_YOLAKAY: { text: t('tripType.yolYolakay'), color: 'orange' },
                  };
                  const l = labels[type] || { text: type, color: 'default' };
                  return <Tag color={l.color}>{l.text}</Tag>;
                },
              },
              {
                title: t('table.route'),
                key: 'route',
                render: (_: unknown, record: any) => {
                  const pickup = record.pickupRegion?.nameUz || record.pickupDistrict?.nameUz || '-';
                  const dest = record.destinationRegion?.nameUz || record.destinationDistrict?.nameUz || '-';
                  return `${pickup} → ${dest}`;
                },
              },
              {
                title: t('common.date'),
                key: 'date',
                width: 120,
                render: (_: unknown, record: any) => {
                  const date = record.requestedAt || record.createdAt;
                  return date ? new Date(date).toLocaleDateString('uz-UZ') : '-';
                },
              },
              {
                title: t('common.price'),
                key: 'price',
                width: 130,
                render: (_: unknown, record: any) => {
                  const price = record.agreedPrice || record.offeredPrice || 0;
                  return price > 0 ? formatCurrency(price) : '-';
                },
              },
              {
                title: t('table.status'),
                dataIndex: 'status',
                key: 'status',
                width: 130,
                render: (status: string) => {
                  const colors: Record<string, string> = {
                    REQUESTED: 'blue',
                    SEARCHING: 'cyan',
                    ACCEPTED: 'geekblue',
                    DRIVER_ASSIGNED: 'purple',
                    IN_PROGRESS: 'orange',
                    COMPLETED: 'green',
                    CANCELLED: 'red',
                    EXPIRED: 'default',
                  };
                  return <Tag color={colors[status] || 'default'}>{status}</Tag>;
                },
              },
            ]}
          />
        </Card>
      </Show>

      {/* Block Modal */}
      <Modal
        title={t('users.blockUser')}
        open={blockModalOpen}
        onCancel={() => {
          setBlockModalOpen(false);
          setBlockReason('');
        }}
        onOk={handleBlock}
        okText={t('common.block')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true, loading: blocking }}
      >
        <p style={{ marginBottom: 16 }}>
          {t('users.enterBlockReason')}:
        </p>
        <Input.TextArea
          value={blockReason}
          onChange={(e) => setBlockReason(e.target.value)}
          placeholder={t('users.blockReasonPlaceholder')}
          rows={4}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={t('users.editUser')}
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={() => editForm.submit()}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            label={t('users.name')}
            name="fullName"
            rules={[{ required: true, message: t('users.nameRequired') }]}
          >
            <Input placeholder={t('users.enterName')} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Wallet Adjustment Modal */}
      <Modal
        title={walletAdjustType === 'add' ? t('users.walletAddTitle') : t('users.walletDeductTitle')}
        open={walletModalOpen}
        onCancel={() => {
          setWalletModalOpen(false);
          walletForm.resetFields();
        }}
        onOk={() => walletForm.submit()}
        okText={walletAdjustType === 'add' ? t('users.addFunds') : t('users.deductFunds')}
        cancelText={t('common.cancel')}
        okButtonProps={{ loading: walletAdjusting, danger: walletAdjustType === 'deduct' }}
      >
        <Form form={walletForm} layout="vertical" onFinish={handleWalletAdjust}>
          <Form.Item
            label={`${t('users.amountLabel')} (${t('common.sum')})`}
            name="amount"
            rules={[
              { required: true, message: t('users.amountRequired') },
              { type: 'number', min: 1, message: t('users.amountPositive') },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder={t('users.amountRequired')}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={(value) => value!.replace(/\s/g, '') as any}
              min={1}
            />
          </Form.Item>
          <Form.Item
            label={t('users.reasonLabel')}
            name="reason"
            rules={[{ required: true, message: t('users.reasonRequired') }]}
          >
            <Input.TextArea placeholder={t('users.reasonRequired')} rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <CallNotesModal
        open={callNotesOpen}
        userId={user?.id ?? null}
        userName={user?.fullName ?? undefined}
        userPhone={user?.phone}
        currentColor={(user as any)?.callIconColor ?? null}
        onClose={() => setCallNotesOpen(false)}
        onChanged={() => refetch()}
      />

      <PushNotificationModal
        open={pushModalOpen}
        userId={user?.id ?? null}
        userName={user?.fullName ?? undefined}
        userPhone={user?.phone}
        hasFcmToken={!!user?.fcmToken}
        onClose={() => setPushModalOpen(false)}
      />
    </>
  );
};
